import React from 'react';
import { Message, Location } from '../types/chat';
import { DefaultWeatherWidget } from './weather/DefaultWeatherWidget';
import { HelperPrompts } from './HelperPrompts';
import { FlightStatusWidget } from './flight/FlightStatusWidget';
import { FlightData } from '../types/flights';
import { dataSyncService } from '../services/dataPersistence';


// Define component instance type
export interface ComponentInstance {
  type: string;
  data: any;
  position: 'after-message' | 'replace-message';
}

// Common props passed to all components
export interface CommonProps {
  onLocationSelect?: (location: Location | null) => void;
  onLocationsUpdate?: (locations: Location[]) => void;
  onActionClick?: (action: string) => void;
  selectedLocation?: Location | null;
}

export class MessageComponentFactory {
  // Component registry - add new components here
  private static componentMap: Record<string, React.ComponentType<any>> = {
    weather: DefaultWeatherWidget,
    help: HelperPrompts,
    flight: FlightStatusWidget  // ADD THIS LINE
    // Add these when ready:
    // locations: LocationCardsContainer,
    // restaurant: RestaurantCards,
    // flight: FlightInfo,
  };

  /**
   * Extract component data from a message
   */
  static extractComponents(message: Message): ComponentInstance[] {

    //console.log('[WEATHER-DEBUG] Extracting components from message:', message.id, message._extractedData);

    const components: ComponentInstance[] = [];

    /* // 1. Weather widget - check both _extractedData and legacy _weatherLocation
    if (message._extractedData?.weather || (message as any)._weatherLocation) {
      components.push({
        type: 'weather',
        data: (message as any)._weatherLocation || message._extractedData?.weather?.location,
        position: 'after-message'
      });
    } */
    // 1. Weather widget - check both _extractedData and legacy _weatherLocation
    if (message._extractedData?.weather || (message as any)._weatherLocation) {
      /* console.log('[WEATHER-DEBUG] Found weather data:', {
        extractedWeather: message._extractedData?.weather,
        legacyWeather: (message as any)._weatherLocation
      }); */
      components.push({
        type: 'weather',
        //data: (message as any)._weatherLocation || message._extractedData?.weather?.location,
         data: {
          location: (message as any)._weatherLocation || message._extractedData?.weather?.location,
          widgetId: (message._extractedData?.weather as any)?.widgetId
        },
        position: 'after-message'
      });
    }

    // 2. Location cards from extracted data
    if (message._extractedData?.locations) {
      components.push({
        type: 'locations',
        data: message._extractedData.locations,
        position: 'after-message'
      });
    }

    // 3. Restaurant cards
    if (message._extractedData?.restaurants) {
      components.push({
        type: 'restaurant',
        data: message._extractedData.restaurants,
        position: 'after-message'
      });
    }

    // 4. Flight info
    if (message._extractedData?.flight) {
      components.push({
        type: 'flight',
        data: message._extractedData.flight,
        position: 'after-message'
      });
    }

    // 5. Help prompts
    if (message.type === 'help') {
      components.push({
        type: 'help',
        data: null,
        position: 'after-message'
      });
    }

    // 6. Image display (replaces message content)
    if (message.type === 'image' && message.imageUrl) {
      components.push({
        type: 'image',
        data: { url: message.imageUrl },
        position: 'replace-message'
      });
    }

    // 7. Flight widget
    if (message._extractedData?.flights) {
      components.push({
        type: 'flight',
        data: message._extractedData.flights,
        position: 'after-message'
      });
    }

    return components;
  }

  /**
   * Render a component with props
   */
  static renderComponent(comp: ComponentInstance, props: CommonProps): React.ReactElement | null {
    const Component = this.componentMap[comp.type];
    
    if (!Component) {
      console.warn(`[MessageComponentFactory] Unknown component type: ${comp.type}`);
      return null;
    }

    // Map props based on component type
    switch (comp.type) {
      /* case 'weather':
        return <Component location={comp.data} />; */
      case 'weather':
      return <Component 
        location={comp.data.location} 
        onWeatherLoaded={(data) => {
          if (!comp.data.widgetId) return;
          
          const widgets = dataSyncService.getWeatherWidgets();
          const widgetIndex = widgets.findIndex(w => w.id === comp.data.widgetId);
          
          if (widgetIndex >= 0) {
            widgets[widgetIndex] = {
              ...widgets[widgetIndex],
              ...data,
              id: comp.data.widgetId,
              timestamp: new Date().toISOString()
            };
            dataSyncService.updateWeatherWidgets([...widgets]);
          }
        }}
      />;
      case 'help':
        return <Component 
          onPromptClick={(template) => props.onActionClick?.(`HELPER_PROMPT:${template}`)} 
        />;
        
      case 'locations':
        // When LocationCardsContainer is ready
        return null; // <Component locations={comp.data} {...props} />;
        
      case 'restaurant':
        // When RestaurantCards is ready
        return null; // <Component restaurants={comp.data} {...props} />;
        
      case 'flight':
        // When FlightInfo is ready
        return <FlightStatusWidget flightData={comp.data} />;
        
      default:
        return <Component data={comp.data} {...props} />;
    }
  }

  /**
   * Check if message has any components
   */
  static hasComponents(message: Message): boolean {
    return this.extractComponents(message).length > 0;
  }

  /**
   * Register a new component type
   */
  static registerComponent(type: string, component: React.ComponentType<any>): void {
    this.componentMap[type] = component;
  }
}